import { apiEndpoints } from '@config/env';
import { error as logError, warn, debug } from './console';
import { getPageShareMirror } from '../store/pageShareStateStore';
import { persistantStore } from '../store/persistantStore';
import { resolveShareComponentPath } from './dashboardShareRegistry';

export const SHARE_ID_PARAM = 'share_id';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface SharingApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export function isValidShareId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value.trim());
}

export function parseShareIdFromSearch(search?: string): string | null {
  if (typeof window === 'undefined' && search == null) {
    return null;
  }
  const raw = search ?? window.location.search;
  const params = new URLSearchParams(raw);
  const id = params.get(SHARE_ID_PARAM) ?? params.get('shareId');
  if (!id) {
    return null;
  }
  const trimmed = id.trim();
  return isValidShareId(trimmed) ? trimmed : null;
}

export function buildShareUrl(shareId: string): string {
  if (typeof window === 'undefined') {
    return `/dashboard?${SHARE_ID_PARAM}=${encodeURIComponent(shareId)}`;
  }
  return `${window.location.origin}/dashboard?${SHARE_ID_PARAM}=${encodeURIComponent(shareId)}`;
}

export function stripShareIdFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.delete(SHARE_ID_PARAM);
  url.searchParams.delete('shareId');
  const query = url.searchParams.toString();
  const path = url.pathname + url.hash;
  window.history.replaceState(null, '', query ? `${path}?${query}` : path);
}

export function normalizeDashboardUrl(): void {
  stripShareIdFromUrl();
}

async function parseJsonResponse<T>(response: Response): Promise<SharingApiEnvelope<T>> {
  const text = await response.text();
  if (!text || !text.trim()) {
    return { success: false, message: 'Empty response' };
  }
  try {
    return JSON.parse(text) as SharingApiEnvelope<T>;
  } catch {
    return { success: false, message: 'Invalid JSON' };
  }
}

export async function createDashboardShare(object: Record<string, unknown>): Promise<string | null> {
  try {
    const { authManager } = await import('./authManager');
    const response = await authManager.makeAuthenticatedRequest(apiEndpoints.app.userSharing, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ object }),
    });
    const body = await parseJsonResponse<{ share_id?: string }>(response);
    if (!response.ok || !body.success) {
      logError('[dashboardUrlUtils] createDashboardShare failed', response.status, body.message);
      return null;
    }
    const id = body.data?.share_id;
    if (typeof id !== 'string' || !isValidShareId(id)) {
      logError('[dashboardUrlUtils] createDashboardShare missing share_id');
      return null;
    }
    return id;
  } catch (e) {
    logError('[dashboardUrlUtils] createDashboardShare error', e);
    return null;
  }
}

export async function resolveDashboardShare(shareId: string): Promise<Record<string, unknown> | null> {
  if (!isValidShareId(shareId)) {
    return null;
  }
  try {
    const { authManager } = await import('./authManager');
    const url = `${apiEndpoints.app.userSharing}/${encodeURIComponent(shareId.trim())}`;
    const response = await authManager.makeAuthenticatedRequest(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await parseJsonResponse<{ object?: Record<string, unknown> }>(response);
    if (!response.ok || !body.success) {
      warn('[dashboardUrlUtils] resolveDashboardShare failed', response.status, body.message);
      return null;
    }
    const obj = body.data?.object;
    if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
      debug('[dashboardUrlUtils] resolveDashboardShare: invalid object');
      return null;
    }
    return obj;
  } catch (e) {
    logError('[dashboardUrlUtils] resolveDashboardShare error', e);
    return null;
  }
}

export interface ShareRouteParams {
  projectId: number;
  className: string;
  datasetId: number;
  date: string;
  menu: string;
  page: string;
  sourceId: number;
  componentPath: string;
  pageKey: string;
}

export function shareObjectToRouteParams(object: Record<string, unknown>): ShareRouteParams | null {
  const projectId = Number(object.project_id ?? object.projectId);
  if (!Number.isFinite(projectId) || projectId <= 0) {
    return null;
  }

  const pageKey = typeof object.page === 'string' ? object.page : '';
  const componentPathFromPayload =
    typeof object.componentPath === 'string' && object.componentPath.trim() !== ''
      ? object.componentPath.replace(/\.(tsx|ts|jsx|js)$/i, '')
      : '';
  const componentPath =
    componentPathFromPayload ||
    (pageKey ? resolveShareComponentPath(pageKey) ?? '' : '');

  return {
    projectId,
    className: typeof object.class_name === 'string' ? object.class_name : typeof object.className === 'string' ? object.className : '',
    datasetId: Number(object.dataset_id ?? object.datasetId ?? 0) || 0,
    date: typeof object.date === 'string' ? object.date : '',
    menu: typeof object.selectedMenu === 'string' ? object.selectedMenu : typeof object.menu === 'string' ? object.menu : '',
    page: typeof object.selectedPage === 'string' ? object.selectedPage : '',
    sourceId: Number(object.source_id ?? object.sourceId ?? 0) || 0,
    componentPath,
    pageKey,
  };
}

type ShareStoreSetters = {
  setSelectedProjectId: (value: number) => void;
  setSelectedClassName: (value: string) => void;
  setSelectedDatasetId: (value: number) => void;
  setSelectedDate: (value: string) => void;
  setSelectedMenu: (value: string) => void;
  setSelectedPage: (value: string) => void;
  setSelectedSourceId: (value: number) => void;
  setSelectedSourceName?: (value: string) => void;
};

export function applyShareRouteParams(
  object: Record<string, unknown>,
  setters: ShareStoreSetters
): ShareRouteParams | null {
  const params = shareObjectToRouteParams(object);
  if (!params) {
    return null;
  }

  if (params.className) {
    setters.setSelectedClassName(params.className);
  }
  setters.setSelectedProjectId(params.projectId);
  setters.setSelectedDatasetId(params.datasetId);
  setters.setSelectedDate(params.date);
  if (params.sourceId > 0) {
    setters.setSelectedSourceId(params.sourceId);
  }
  const sourceName = typeof object.selectedSourceName === 'string' ? object.selectedSourceName.trim() : '';
  if (sourceName && setters.setSelectedSourceName) {
    setters.setSelectedSourceName(sourceName);
  }
  if (params.menu) {
    setters.setSelectedMenu(params.menu);
  }
  if (params.page) {
    setters.setSelectedPage(params.page);
  }

  return params;
}

export function buildDashboardShareObject(pageKey: string, pageState?: Record<string, unknown>): Record<string, unknown> {
  const datasetId = persistantStore.selectedDatasetId();
  const mode =
    datasetId > 0 ? 'dataset' : (persistantStore.selectedDate() || '').trim() !== '' ? 'day' : 'project';

  const base: Record<string, unknown> = {
    v: 2,
    page: pageKey,
    project_id: persistantStore.selectedProjectId(),
    projectId: persistantStore.selectedProjectId(),
    class_name: persistantStore.selectedClassName(),
    className: persistantStore.selectedClassName(),
    mode,
    dataset_id: datasetId,
    datasetId,
    date: persistantStore.selectedDate(),
    source_id: persistantStore.selectedSourceId(),
    sourceId: persistantStore.selectedSourceId(),
    selectedMenu: persistantStore.selectedMenu(),
    selectedPage: persistantStore.selectedPage(),
    menu: persistantStore.selectedMenu(),
  };

  const mirrored = getPageShareMirror(pageKey);
  if (mirrored && Object.keys(mirrored).length > 0) {
    Object.assign(base, mirrored);
  }
  if (pageState && Object.keys(pageState).length > 0) {
    Object.assign(base, pageState);
  }

  return base;
}

export async function copyShareUrlToClipboard(shareId: string): Promise<boolean> {
  const url = buildShareUrl(shareId);
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (e) {
    warn('[dashboardUrlUtils] clipboard write failed', e);
    return false;
  }
}

export function buildLoginRedirectUrl(): string {
  if (typeof window === 'undefined') {
    return '/login';
  }
  const path = window.location.pathname + window.location.search + window.location.hash;
  if (!path || path === '/login') {
    return '/login';
  }
  return `/login?redirect=${encodeURIComponent(path)}`;
}
